import Menu, { MenuProps } from "@mui/material/Menu";
import { useColorScheme } from "@mui/material/styles";
import { MouseEvent, memo, useCallback, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import { routers } from "@/configs";
import { useNotifications } from "@/hooks";

import { NotificationBell } from "./components/NotificationBell";
import { NotificationMenuBody } from "./components/NotificationMenuBody";
import { NotificationMenuFooter } from "./components/NotificationMenuFooter";
import { NotificationMenuHeader } from "./components/NotificationMenuHeader";

const NotificationComponent = () => {
  const navigate = useNavigate();
  const { mode } = useColorScheme();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const { notifications, unreadCount, isLoading, markOneAsRead, markAllRead } = useNotifications();
  const open = !!anchorEl;

  const unreadNotifications = useMemo(
    () => notifications.filter((n) => !n.isRead),
    [notifications],
  );

  const handleClick = useCallback((event: MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  }, []);

  const handleClose = useCallback(() => {
    setAnchorEl(null);
  }, []);

  const handleSeeAll = useCallback(() => {
    navigate(routers.Notifications);
    handleClose();
  }, [navigate, handleClose]);

  const handleNotificationClick = useCallback(() => {
    navigate(routers.Notifications);
    handleClose();
  }, [navigate, handleClose]);

  const menuProps: MenuProps = {
    anchorEl,
    id: "notification-menu",
    open,
    onClose: handleClose,
    onClick: handleClose,
    transformOrigin: { horizontal: "right", vertical: "top" },
    anchorOrigin: { horizontal: "right", vertical: "bottom" },
    MenuListProps: { sx: { p: 0 } },
    className: mode,
    PaperProps: {
      sx: {
        // Never wider than the phone it opens on.
        width: "min(350px, calc(100vw - 16px))",
        maxHeight: 400,
        overflow: "auto",
        mt: 1.5,
        ml: 0.75,
        "& .MuiMenuItem-root": {
          typography: "body2",
          borderRadius: 0.75,
        },
      },
    },
  };

  return (
    <>
      <NotificationBell unreadCount={unreadCount} open={open} mode={mode} onClick={handleClick} />
      <Menu {...menuProps}>
        <NotificationMenuHeader
          disabled={unreadCount === 0 || isLoading}
          onMarkAllRead={markAllRead}
        />
        <NotificationMenuBody
          isLoading={isLoading}
          notifications={unreadNotifications}
          onNotificationClick={handleNotificationClick}
          onMarkRead={markOneAsRead}
        />
        <NotificationMenuFooter onSeeAll={handleSeeAll} />
      </Menu>
    </>
  );
};

export default memo(NotificationComponent);
